const API_REQUEST_TIMEOUT_MS = 28_000;

export class ApiRequestTimeoutError extends Error {
  constructor() {
    super('The server took too long to respond. Check your connection and try again.');
    this.name = 'ApiRequestTimeoutError';
  }
}

export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export function createApiFetch(
  timeoutMs = API_REQUEST_TIMEOUT_MS,
  fetchImpl: FetchLike = globalThis.fetch
): FetchLike {
  return async (input, init: RequestInit = {}) => {
    const controller = new AbortController();
    const callerSignal = init.signal;
    let timedOut = false;

    const abortFromCaller = () => controller.abort(callerSignal?.reason);
    if (callerSignal?.aborted) abortFromCaller();
    else callerSignal?.addEventListener('abort', abortFromCaller, { once: true });

    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    try {
      return await fetchImpl(input, { ...init, signal: controller.signal });
    } catch (error) {
      if (timedOut && !callerSignal?.aborted) throw new ApiRequestTimeoutError();
      throw error;
    } finally {
      clearTimeout(timeout);
      callerSignal?.removeEventListener('abort', abortFromCaller);
    }
  };
}

export const apiFetch = createApiFetch();
